import { useSearchParams } from 'next/navigation';
import { ReactNode, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Paper, Step, Stack, StepLabel, Stepper, Typography } from '@mui/material';
import { useRegistrationStore } from '@/store/pages/registrationStore/registrationStore';
import { RegistrationStep } from '@/types/enums/forms/registration/registrationWizard';
import {
    ContactDetailsForm,
    RegistrationPersonalDetailsForm,
    RegistrationProfessionalDetailsForm,
    RegistrationPersonalDetailsFormSkeleton,
    RegistrationIdentityForm,
} from '@/components/forms';
import { useHasHydrated, usePhrase } from '@/hooks/general';
import { Phrases } from '@/consts/phrases';
import { PROFESSIONS, REGISTRATION_STEP } from '@/consts/forms/registration';
import {
    IAllRegistrationData,
    DefaultPageProps,
    RegistrationSteps as RegistrationStepsType,
    Phrase,
    IWalletPersonalData,
    IRegistrationTemplateResponse,
    IRegistrationPersonalDetails,
} from 'types/types';
import { useRegistrationDetailsQuery } from '@/hooks/queries/registration/generalRegistrationQueries';
import { AddressForm } from 'components/forms/forms/registration/AddressForm/AddressForm';
import { getThemeProps } from '@/components/layoutUtil';
import { getHomeQueryParams } from '@/utils/getHomeQueryParams';
import { PERSONAL_DETAILS_PHRASES } from '@/consts/phrases/registrationPhrases';
import { isFeatureEnabled } from '@/config';
interface RegistrationStepsProps {
    dictionary?: DefaultPageProps['dictionary'];
    nonMemberPersonalData?: Partial<IWalletPersonalData>;
    registrationTemplate?: IRegistrationTemplateResponse;
    paymentToken?: string | undefined;
}

type CreateStepsFn = (
    t: ReturnType<typeof usePhrase>['t'],
    dictionary?: DefaultPageProps['dictionary'],
    registrationData?: IAllRegistrationData,
) => Map<
    RegistrationStep,
    {
        step: number;
        stepName: string;
        id: number;
        title: Phrase;
        optional: boolean;
        component: React.ReactElement;
    }
>;

export const RegistrationSteps = ({
    dictionary,
    nonMemberPersonalData,
    registrationTemplate,
    paymentToken,
}: RegistrationStepsProps) => {
    const { t } = usePhrase();

    const searchParams = useSearchParams();

    const hasHydrated = useHasHydrated();

    const { currentStep, goToStep, setRegistrationId, reset } = useRegistrationStore((state) => ({
        currentStep: state.step,
        goToStep: state.goToStep,
        setRegistrationId: state.setRegistrationId,
        reset: state.reset,
    }));

    const { registrationId } = getHomeQueryParams(searchParams);

    const [isInitialRegistrationIdCheck, setIsInitialRegistrationIdCheck] = useState(true);

    const {
        data: registrationData,
        revalidate,
        isLoading: isRegistrationDataLoading,
    } = useRegistrationDetailsQuery(
        true,
        0,
        isInitialRegistrationIdCheck ? registrationId : undefined,
        false,
    );

    const [isRegistrationDataRevalidating, setIsRegistrationDataRevalidating] = useState(false);

    const revalidateRegistrationData = useCallback(async () => {
        setIsRegistrationDataRevalidating(true);
        await revalidate();
        setIsRegistrationDataRevalidating(false);
    }, [revalidate]);

    const [registrationDataStep, setRegistrationDataStep] = useState(registrationData?.step);
    const [professionChanged, setProfessionChanged] = useState(false);
    const [hasSelectedPatientConsumer, setHasSelectedPatientConsumer] = useState(false);
    const [mobileIdentity, setMobileIdentity] = useState(false);
    const [yotiVisible, setYotiVisible] = useState(false);
    const [registrationDataError, setRegistrationDataError] = useState(false);

    const registrationFormRef = useRef<HTMLDivElement>(null);

    const scrollToTopOfRegistration = () => {
        const registrationForm = registrationFormRef.current;
        if (registrationForm) {
            const headerHeight = document.querySelector('header')?.clientHeight;
            registrationForm.scrollIntoView(true);
            if (headerHeight) window.scrollBy(0, -(headerHeight + 10));
        }
    };

    useEffect(() => {
        if (registrationDataStep) {
            scrollToTopOfRegistration();
        }
    }, [currentStep, registrationDataStep]);

    const isInitialRenderWithQueryParam =
        !isRegistrationDataLoading && isInitialRegistrationIdCheck && registrationId;

    if (isInitialRenderWithQueryParam) {
        if (!registrationData) {
            setRegistrationDataError(true);
            reset();
        } else {
            setRegistrationId(registrationId);
        }
        setIsInitialRegistrationIdCheck(false);
    }

    const {
        homeLayout: {
            registrationForm: { fullwidthForm, showStepTitle, stepperOrientation },
            iconsBlock: { position: iconsBlockPosition },
            picBlock: { position: pickBlockPosition },
        },
    } = getThemeProps();

    const isIconsBlockLeftSide = iconsBlockPosition === 'left';
    const isPicBlockLeftSide = pickBlockPosition === 'left';

    const isYotiCompleted =
        !!registrationData &&
        (registrationData?.step === RegistrationStepsType.Address ||
            !!registrationData?.yotiAddress1 ||
            !!registrationData?.yotiZip ||
            !!registrationData?.yotiCity ||
            !!registrationData?.yotiStateCode ||
            !!registrationData?.yotiCountryCode);

    const isPatientConsumer =
        hasSelectedPatientConsumer ||
        registrationData?.professionId === PROFESSIONS.PATIENT_CONSUMER;

    const professionalDetailsPhrase = isPatientConsumer
        ? t(Phrases.ADDITIONAL_DETAILS)
        : t(Phrases.PROFESSIONAL_DETAILS);

    const onPersonalDetailsSubmit = useCallback(
        (data: Partial<IRegistrationPersonalDetails>) => {
            setRegistrationDataError(false);
            setProfessionChanged(data.professionId !== registrationData?.professionId);
        },
        [registrationData?.professionId, setRegistrationDataError, setProfessionChanged],
    );

    const createSteps: CreateStepsFn = useMemo(
        () => (t, dictionary, registrationData) => {
            const useNewFlow = isFeatureEnabled('registrationCompleteFlow');
            const entries: Array<any> = [
                [
                    RegistrationStep.PersonalDetails,
                    {
                        step: 0,
                        stepName: REGISTRATION_STEP[RegistrationStep.PersonalDetails],
                        id: Phrases.PERSONAL_DETAILS,
                        title: t(Phrases.PERSONAL_DETAILS),
                        optional: false,
                        component: (
                            <>
                                {registrationDataError && (
                                    <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
                                        {t(
                                            PERSONAL_DETAILS_PHRASES.REGISTRATION_LINK_EXPIRED_ERROR,
                                        )}
                                    </Alert>
                                )}
                                {showStepTitle && (
                                    <Typography
                                        variant="h4"
                                        sx={{ display: { xs: 'none', md: 'block' }, mb: 4 }}
                                    >
                                        {t(Phrases.PERSONAL_DETAILS)}
                                    </Typography>
                                )}
                                {hasHydrated && dictionary && !isRegistrationDataLoading ? (
                                    <RegistrationPersonalDetailsForm
                                        registrationData={registrationData}
                                        nonMemberPersonalData={nonMemberPersonalData}
                                        registrationTemplate={registrationTemplate}
                                        dictionary={dictionary}
                                        onPersonalDetailsSubmit={onPersonalDetailsSubmit}
                                        onProfessionChange={(selectedProfId) =>
                                            setHasSelectedPatientConsumer(
                                                selectedProfId === PROFESSIONS.PATIENT_CONSUMER,
                                            )
                                        }
                                        revalidateRegistrationData={revalidateRegistrationData}
                                    />
                                ) : (
                                    <RegistrationPersonalDetailsFormSkeleton />
                                )}
                            </>
                        ),
                    },
                ],
                [
                    RegistrationStep.ProfessionalDetails,
                    {
                        step: 1,
                        stepName: REGISTRATION_STEP[RegistrationStep.ProfessionalDetails],
                        id: Phrases.PROFESSIONAL_DETAILS,
                        title: professionalDetailsPhrase,
                        optional: true,
                        component: (
                            <>
                                {showStepTitle && (
                                    <Typography
                                        variant="h4"
                                        sx={{ display: { xs: 'none', md: 'block' }, mb: 4 }}
                                    >
                                        {professionalDetailsPhrase}
                                    </Typography>
                                )}
                                {hasHydrated && dictionary && (
                                    <RegistrationProfessionalDetailsForm
                                        registrationData={registrationData}
                                        nonMemberPersonalData={nonMemberPersonalData}
                                        dictionary={dictionary}
                                        professionChanged={professionChanged}
                                        revalidateRegistrationData={revalidateRegistrationData}
                                    />
                                )}
                            </>
                        ),
                    },
                ],
                [
                    RegistrationStep.ContactDetails,
                    {
                        step: 2,
                        stepName: REGISTRATION_STEP[RegistrationStep.ContactDetails],
                        id: Phrases.CONTACT_DETAILS,
                        title: t(Phrases.CONTACT_DETAILS),
                        optional: false,
                        component: (
                            <>
                                {showStepTitle && (
                                    <Typography
                                        variant="h4"
                                        sx={{ display: { xs: 'none', md: 'block' }, mb: 4 }}
                                    >
                                        {t(Phrases.CONTACT_DETAILS)}
                                    </Typography>
                                )}
                                <ContactDetailsForm
                                    registrationData={registrationData}
                                    nonMemberPersonalData={nonMemberPersonalData}
                                    revalidateRegistrationData={revalidateRegistrationData}
                                />
                            </>
                        ),
                    },
                ],
                // Identity step (conditionally included)
                [
                    RegistrationStep.Address,
                    {
                        step: useNewFlow ? 3 : 4,
                        stepName: REGISTRATION_STEP[RegistrationStep.Address],
                        id: Phrases.ADDRESS,
                        title: t(Phrases.ADDRESS),
                        optional: false,
                        component: (
                            <>
                                {showStepTitle && (
                                    <Typography
                                        variant="h4"
                                        sx={{ display: { xs: 'none', md: 'block' }, mb: 4 }}
                                    >
                                        {t(Phrases.ADDRESS)}
                                    </Typography>
                                )}
                                <AddressForm
                                    registrationData={registrationData}
                                    nonMemberPersonalData={nonMemberPersonalData}
                                    paymentToken={paymentToken}
                                    dictionary={dictionary}
                                    isYotiCompleted={isYotiCompleted}
                                />
                            </>
                        ),
                    },
                ],
            ];

            if (!useNewFlow) {
                entries.splice(3, 0, [
                    RegistrationStep.Identity,
                    {
                        step: 3,
                        stepName: REGISTRATION_STEP[RegistrationStep.Identity],
                        id: Phrases.IDENTITY,
                        title: t(Phrases.IDENTITY),
                        optional: false,
                        component: (
                            <>
                                {showStepTitle && (
                                    <Typography
                                        variant="h4"
                                        sx={{ display: { xs: 'none', md: 'block' }, mb: 4 }}
                                    >
                                        {t(Phrases.IDENTITY)}
                                    </Typography>
                                )}
                                <RegistrationIdentityForm
                                    relevantIdRequestId={registrationData?.relevantIdRequestId}
                                    isRegistrationDataLoading={
                                        isRegistrationDataLoading || isRegistrationDataRevalidating
                                    }
                                    revalidateRegistrationData={revalidateRegistrationData}
                                    state={{
                                        mobileIdentity,
                                        setMobileIdentity,
                                        yotiVisible,
                                        setYotiVisible,
                                        isYotiCompleted,
                                    }}
                                />
                            </>
                        ),
                    },
                ]);
            }

            // reindexing steps to be consecutive in case Identity is removed
            const reindexed = entries.map(([key, value], idx) => {
                return [key as RegistrationStep, { ...value, step: idx } as any] as const;
            });
            return new Map(reindexed) as Map<
                RegistrationStep,
                {
                    step: number;
                    stepName: string;
                    id: number;
                    title: Phrase;
                    optional: boolean;
                    component: React.ReactElement;
                }
            >;
        },
        [
            registrationDataError,
            showStepTitle,
            hasHydrated,
            isRegistrationDataLoading,
            nonMemberPersonalData,
            registrationTemplate,
            revalidateRegistrationData,
            professionChanged,
            setHasSelectedPatientConsumer,
            paymentToken,
            isYotiCompleted,
            mobileIdentity,
            setMobileIdentity,
            yotiVisible,
            setYotiVisible,
            isRegistrationDataRevalidating,
            professionalDetailsPhrase,
            onPersonalDetailsSubmit,
        ],
    );

    useEffect(() => {
        const stepName = registrationData?.step;
        if (!stepName) return;

        const useNewFlow = isFeatureEnabled('registrationCompleteFlow');
        const targetStepName =
            useNewFlow && stepName === RegistrationStepsType.Identity
                ? RegistrationStepsType.Address
                : stepName;

        if (registrationDataStep === targetStepName) return;

        setRegistrationDataStep(targetStepName);

        let nextEnum: RegistrationStep | undefined;
        switch (targetStepName) {
            case RegistrationStepsType.Personal:
                nextEnum = RegistrationStep.PersonalDetails;
                break;
            case RegistrationStepsType.Professional:
                nextEnum = RegistrationStep.ProfessionalDetails;
                break;
            case RegistrationStepsType.Contact:
                nextEnum = RegistrationStep.ContactDetails;
                break;
            case RegistrationStepsType.Identity:
                nextEnum = RegistrationStep.Identity;
                break;
            case RegistrationStepsType.Address:
                nextEnum = RegistrationStep.Address;
                break;
            default:
                nextEnum = undefined;
        }

        if (typeof nextEnum === 'number') {
            goToStep(nextEnum);
        }
    }, [registrationData?.step, registrationDataStep, goToStep]);

    const stepsMap = useMemo(
        () => createSteps(t, dictionary, registrationData),
        [createSteps, t, dictionary, registrationData],
    );

    const StepperComponent = ({
        orientation,
        testId = 'reg-stepper',
    }: {
        orientation?: 'horizontal' | 'vertical';
        testId?: string;
    }) => (
        <Stepper
            data-testid={testId}
            activeStep={stepsMap.get(currentStep)?.step}
            orientation={orientation || stepperOrientation}
        >
            {Array.from(stepsMap.values()).map((step) => {
                const stepProps: { completed?: boolean } = {};
                const labelProps: {
                    optional?: ReactNode;
                } = {};

                return (
                    <Step key={step.id} {...stepProps}>
                        <StepLabel {...labelProps}>{step.title}</StepLabel>
                    </Step>
                );
            })}
        </Stepper>
    );

    const isFullWidth = fullwidthForm && !isIconsBlockLeftSide && !isPicBlockLeftSide;

    return (
        <Box ref={registrationFormRef}>
            {isFullWidth ? (
                <Stack
                    direction="row"
                    sx={{
                        justifyContent: 'space-between',
                        flexWrap: ['wrap', 'wrap', 'nowrap'],
                    }}
                >
                    <Paper
                        className="registrationBox"
                        sx={{
                            display: ['none', 'none', 'inline-flex'],
                            p: 4,
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            minWidth: 280,
                        }}
                    >
                        <StepperComponent testId="fullwidth_stepper" />
                    </Paper>
                    <Paper className="registrationBox" sx={{ width: '100%', p: 3, ml: [0, 0, 3] }}>
                        <Box sx={{ display: { md: 'none' }, mb: 4 }}>
                            <StepperComponent
                                orientation="horizontal"
                                testId="fullwidth_stepper_mobile"
                            />
                        </Box>
                        <StepForm stepsMap={stepsMap} currentStep={currentStep} />
                    </Paper>
                </Stack>
            ) : (
                <Paper className="registrationBox" sx={{ p: 3 }}>
                    <Box sx={{ mb: 4 }}>
                        <StepperComponent testId="inline_column_stepper" />
                    </Box>
                    <StepForm stepsMap={stepsMap} currentStep={currentStep} />
                </Paper>
            )}
        </Box>
    );
};

interface StepFormProps {
    stepsMap: Map<
        RegistrationStep,
        {
            step: number;
            stepName: string;
            id: number;
            title: Phrase;
            optional: boolean;
            component: React.ReactElement;
        }
    >;
    currentStep: RegistrationStep;
}

const _StepForm = ({ stepsMap, currentStep }: StepFormProps) => (
    <Box component="div">{stepsMap.get(currentStep)?.component}</Box>
);

const StepForm = memo(_StepForm);
